import React from 'react';
import { Resource } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';

interface ResourceIdentityBlockProps {
  resource: Resource;
  size?: 'sm' | 'md' | 'lg';
  /** If provided, matched skills are highlighted and proficiency pips shown */
  requiredSkills?: string[];
}

/** Small filled-dot proficiency indicator — 5 pips, filled up to `rating` */
function ProficiencyPips({ rating, highlighted }: { rating: number; highlighted: boolean }) {
  return (
    <span className="inline-flex items-center gap-[2px] ml-1" aria-label={`Proficiency ${rating} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={
            i < rating
              ? highlighted
                ? 'inline-block h-1.5 w-1.5 rounded-full bg-teal-500'
                : 'inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50'
              : 'inline-block h-1.5 w-1.5 rounded-full bg-border'
          }
        />
      ))}
    </span>
  );
}

export function ResourceIdentityBlock({
  resource,
  size = 'md',
  requiredSkills = [],
}: ResourceIdentityBlockProps) {
  const initials = resource.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isSmall = size === 'sm';
  const isLarge = size === 'lg';

  const requiredLower = requiredSkills.map(s => s.toLowerCase());

  if (isLarge) {
    return (
      <div className="flex items-start gap-3.5">
        <Avatar className="h-12 w-12 shrink-0">
          <AvatarImage src={resource.photoUrl} />
          <AvatarFallback className="text-sm">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 space-y-1">
          {/* Name + type badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-semibold leading-tight truncate">{resource.name}</p>
            {resource.type && (
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0 h-5 font-normal capitalize shrink-0"
              >
                {resource.type}
              </Badge>
            )}
          </div>

          {/* Role · level · department */}
          <p className="text-sm text-muted-foreground leading-snug">
            {resource.role}
            {resource.level && (
              <span className="text-muted-foreground/70"> · {resource.level}</span>
            )}
            {resource.department && (
              <span className="text-muted-foreground/50"> · {resource.department}</span>
            )}
          </p>

          {/* Location */}
          {resource.location && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              {resource.location}
            </span>
          )}

          {/* Skills — show all required first (highlighted), then others up to limit */}
          {resource.skills && resource.skills.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
              {(() => {
                const matched = resource.skills.filter(s =>
                  requiredLower.includes(s.name.toLowerCase())
                );
                const others = resource.skills.filter(
                  s => !requiredLower.includes(s.name.toLowerCase())
                );
                // Show matched skills first, then fill up to 4 total with others
                const visible = [...matched, ...others].slice(0, 4);
                const remaining = resource.skills.length - visible.length;

                return (
                  <>
                    {visible.map(s => {
                      const isMatched =
                        requiredLower.length > 0 &&
                        requiredLower.includes(s.name.toLowerCase());
                      return (
                        <span
                          key={s.name}
                          className={[
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            isMatched
                              ? 'bg-teal-50 text-teal-700 border border-teal-200'
                              : 'bg-muted text-muted-foreground border border-border',
                          ].join(' ')}
                        >
                          {s.name}
                          {s.rating !== undefined && (
                            <ProficiencyPips rating={s.rating} highlighted={isMatched} />
                          )}
                        </span>
                      );
                    })}
                    {remaining > 0 && (
                      <span className="text-xs text-muted-foreground">
                        +{remaining} more
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5">
      <Avatar className={isSmall ? 'h-7 w-7 shrink-0' : 'h-9 w-9 shrink-0'}>
        <AvatarImage src={resource.photoUrl} />
        <AvatarFallback
          className={isSmall ? 'text-xs' : 'text-sm'}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-none truncate">{resource.name}</p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {resource.role}
          {!isSmall && resource.department && (
            <span className="text-muted-foreground/60"> · {resource.department}</span>
          )}
        </p>
      </div>
    </div>
  );
}
